import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, Menu, Plus, SearchLg, TextInput, XClose,
} from '@opendate-app/setlist';
import { KPI_GROUPS, MAX_KPI_CARDS, MIN_KPI_CARDS } from '../../utils/dashboardUtils';

const SEARCH_ID = 'kpi-picker-search';
const MENU_NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Escape', 'Tab'];

const checkboxIcon = (checked) => function KpiCheckbox() {
  return (
    <span style={{ display: 'inline-flex', pointerEvents: 'none' }}>
      <Checkbox size="sm" checked={checked} onChange={() => {}} tabIndex={-1} aria-hidden="true" />
    </span>
  );
};
const CheckedIcon = checkboxIcon(true);
const UncheckedIcon = checkboxIcon(false);

function KpiPicker({ catalog, keys, onToggle }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const atMin = keys.length <= MIN_KPI_CARDS;
  const atMax = keys.length >= MAX_KPI_CARDS;
  const slotsLeft = MAX_KPI_CARDS - keys.length;

  // WHY: Menu focuses its first item when it opens; move focus to search once it has.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => document.getElementById(SEARCH_ID)?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);

  const changeOpen = (next) => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const q = query.trim().toLowerCase();
  const items = KPI_GROUPS.flatMap((group) => {
    const matches = catalog.filter((c) => c.group === group && (!q || `${c.label} ${c.caption}`.toLowerCase().includes(q)));
    return matches.map((c) => {
      const on = keys.includes(c.key);
      return {
        key: c.key,
        label: c.label,
        description: `${group} · ${c.caption}`,
        shortcut: c.value,
        icon: on ? CheckedIcon : UncheckedIcon,
        disabled: on ? atMin : atMax,
        onClick: () => onToggle(c.key),
      };
    });
  });

  const header = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--sl-text-primary)' }}>Add a KPI card</span>
        <Button variant="tertiary" size="sm" aria-label="Close" onClick={() => changeOpen(false)}><XClose width={16} height={16} /></Button>
      </div>
      <TextInput
        id={SEARCH_ID}
        size="sm"
        placeholder="Search metrics"
        leadingIcon={SearchLg}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (!MENU_NAV_KEYS.includes(e.key)) e.stopPropagation(); }}
      />
      {items.length === 0 && (
        <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary)' }}>{`No metrics match “${query}”.`}</span>
      )}
      <span style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>
        {atMax
          ? `${MAX_KPI_CARDS}-card limit reached. Uncheck one to swap.`
          : `${keys.length} of ${MAX_KPI_CARDS} selected · minimum ${MIN_KPI_CARDS}`}
      </span>
    </div>
  );

  return (
    <div className="kpi-picker-slot">
      <Menu
        open={open}
        onOpenChange={changeOpen}
        closeOnSelect={false}
        minWidth={320}
        header={header}
        items={items}
        trigger={(
          <button
            type="button"
            style={{
              width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
              borderRadius: 12, border: `1px dashed ${open ? 'var(--sl-border-brand)' : 'var(--sl-border-primary)'}`,
              background: open ? 'var(--sl-bg-brand-primary)' : 'transparent', color: 'var(--sl-text-brand)', cursor: 'pointer',
            }}
          >
            <Plus width={20} height={20} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Add card</span>
            <span style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{`${slotsLeft} ${slotsLeft === 1 ? 'slot' : 'slots'} left`}</span>
          </button>
        )}
      />
    </div>
  );
}

export default KpiPicker;
